// src/pages/WarmupDetail.tsx
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Grid, Box, Alert, CircularProgress, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';

interface Action {
  action_id: string;
  action_type: string;
  account_name: string;
  stage: string;
  metadata: Record<string, any>;
  success: boolean;
  error?: string;
  created_at: string;
}

interface Detail {
  campaign_id: string;
  status: string;
  current_day: number;
  total_days: number;
  current_stage: string;
  total_actions: number;
  total_subscriptions: number;
  total_reactions: number;
  total_comments: number;
  total_messages: number;
  total_inter_group_messages: number;
  total_inter_private_messages: number;
  total_inter_reactions: number;
  total_channels_searched: number;
  total_channels_joined: number;
  total_groups_joined: number;
  accounts_count: number;
  accounts_stats: Record<string, any>;
  errors: number;
  warnings: string[];
  started_at?: string;
  last_update?: string;
  completed_at?: string;
  actions: Action[];
}

const WarmupDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    if (!jobId) return;
    try {
      const [statsRes, actionsRes] = await Promise.all([
        api.get(`/warmup/status/${jobId}`),
        api.get(`/warmup/actions/${jobId}`)
      ]);

      setData({
        ...statsRes.data,
        actions: actionsRes.data.actions || []
      });
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Кампания не найдена или удалена');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!jobId) {
      navigate('/warmup');
      return;
    }
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [jobId, navigate]);

  if (!jobId) return null;

  if (loading) {
    return (
      <Container sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress size={70} />
        <Typography variant="h6" mt={3}>Загрузка статистики прогрева...</Typography>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Кампания не найдена или была удалена'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/warmup')}>
          Назад к прогреву аккаунтов
        </Button>
      </Container>
    );
  }

  const progressDay = Math.round((data.current_day / data.total_days) * 100);

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/warmup')} sx={{ mb: 4 }}>
        Назад к прогреву аккаунтов
      </Button>

      <Paper elevation={4} sx={{ p: 5, borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Typography variant="h4" fontWeight="bold" color="#1E3A8A" gutterBottom>
          Прогрев #{data.campaign_id.slice(0, 8)}
        </Typography>
        <Typography color="#4B5563" fontSize="1.1rem" mb={4}>
          Аккаунтов: <strong>{data.accounts_count}</strong> ·
          День: <strong>{data.current_day} из {data.total_days}</strong> ·
          Этап: <strong>{data.current_stage}</strong>

        </Typography>

        {/* Прогресс дня */}
        <Box sx={{ my: 6 }}>
          <Typography variant="subtitle1" color="#1E40AF" fontWeight={600} gutterBottom>
            Прогресс прогрева
          </Typography>
          <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" color="#F59E0B">
              {progressDay}%
            </Typography>
            <Typography color="text.secondary">День {data.current_day} из {data.total_days}</Typography>
          </Paper>
        </Box>

        {/* Основная активность */}
        <Box sx={{ my: 6 }}>
          <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
            Основные действия
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#3B82F6">
                  {data.total_actions}
                </Typography>
                <Typography color="text.secondary">Всего действий</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#10B981">
                  {data.total_subscriptions}
                </Typography>
                <Typography color="text.secondary">Подписки</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#8B5CF6">
                  {data.total_reactions}
                </Typography>
                <Typography color="text.secondary">Реакции</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                <Typography variant="h4" fontWeight="bold" color="#EC4899">
                  {data.total_comments}
                </Typography>
                <Typography color="text.secondary">Комментарии</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Общение между аккаунтами */}
        {(data.total_inter_group_messages > 0 || data.total_inter_private_messages > 0) && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Общение между аккаунтами
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#6366F1">
                    {data.total_inter_group_messages}
                  </Typography>
                  <Typography color="text.secondary">Сообщений в группе</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#F59E0B">
                    {data.total_inter_private_messages}
                  </Typography>
                  <Typography color="text.secondary">Личных сообщений</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#10B981">
                    {data.total_inter_reactions}
                  </Typography>
                  <Typography color="text.secondary">Реакций в общении</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Поиск и подписки на каналы */}
        {(data.total_channels_searched > 0 || data.total_channels_joined > 0 || data.total_groups_joined > 0) && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Поиск и подписки
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#8B5CF6">
                    {data.total_channels_searched}
                  </Typography>
                  <Typography color="text.secondary">Каналов найдено</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#10B981">
                    {data.total_channels_joined}
                  </Typography>
                  <Typography color="text.secondary">Каналов подписано</Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="#F59E0B">
                    {data.total_groups_joined}
                  </Typography>
                  <Typography color="text.secondary">Групп подписано</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Предупреждения */}
        {data.warnings.length > 0 && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#DC2626" fontWeight={600} gutterBottom>
              Предупреждения
            </Typography>
            {data.warnings.map((warning, i) => (
              <Alert key={i} severity="warning" sx={{ mb: 1 }}>
                <WarningIcon sx={{ mr: 1 }} />
                {warning}
              </Alert>
            ))}
          </Box>
        )}

        {/* Ошибки */}
        {data.errors > 0 && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#DC2626" fontWeight={600} gutterBottom>
              Ошибки: {data.errors}
            </Typography>
          </Box>
        )}

        {/* Последние действия */}
        {data.actions.length > 0 && (
          <Box sx={{ my: 6 }}>
            <Typography variant="h6" color="#1E40AF" fontWeight={600} gutterBottom>
              Последние действия
            </Typography>
            <TableContainer component={Paper} elevation={2}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Время</strong></TableCell>
                    <TableCell><strong>Тип</strong></TableCell>
                    <TableCell><strong>Аккаунт</strong></TableCell>
                    <TableCell><strong>Этап</strong></TableCell>
                    <TableCell><strong>Статус</strong></TableCell>
                    <TableCell><strong>Детали</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.actions.slice(0, 20).map(a => (
                    <TableRow key={a.action_id}>
                      <TableCell>{new Date(a.created_at).toLocaleString('ru-RU')}</TableCell>
                      <TableCell>{a.action_type}</TableCell>
                      <TableCell>{a.account_name}</TableCell>
                      <TableCell>{a.stage}</TableCell>
                      <TableCell>
                        {a.success ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          <ErrorIcon fontSize="small" color="error" />
                        )}
                      </TableCell>
                      <TableCell>
                        {Object.keys(a.metadata).length > 0 ? (
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                              {Object.entries(a.metadata).map(([key, value]) => (
                                  <Typography key={key} variant="body2" sx={{color: '#4B5563'}}>
                                    <strong>{key}:</strong>{' '}
                                    {typeof value === 'string' && value.startsWith('https://')
                                        ? <a href={value} target="_blank" rel="noopener noreferrer"
                                             style={{color: '#3B82F6'}}>{value}</a>
                                        : value}
                                  </Typography>
                              ))}
                            </Box>
                        ) : (
                            '—'
                        )}
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Время */}
        <Box sx={{ p: 4, bgcolor: '#F9FAFB', borderRadius: 2 }}>
          <Typography variant="h6" color="#1E40AF" gutterBottom>Время</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Запущен</Typography>
              <Typography fontWeight={600}>{data.started_at ? new Date(data.started_at).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Последнее обновление</Typography>
              <Typography fontWeight={600}>{data.last_update ? new Date(data.last_update).toLocaleString('ru-RU') : '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography color="text.secondary">Завершён</Typography>
              <Typography fontWeight={600} color={data.completed_at ? '#10B981' : 'inherit'}>
                {data.completed_at ? new Date(data.completed_at).toLocaleString('ru-RU') : '—'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default WarmupDetail;